import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MessageSquare, Send, Users, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { smsApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

// SMS templates (will move to backend later)
const smsTemplates = [
  {
    id: 'sms1',
    name: 'Appointment Reminder',
    content: 'Hi {name}! Reminder: Your {treatment} appointment is tomorrow at {time}. Reply C to confirm or R to reschedule.',
    category: 'Reminders'
  },
  {
    id: 'sms2',
    name: 'Birthday Special',
    content: 'Happy Birthday {name}! Enjoy 20% off any treatment this month.',
    category: 'Promotions'
  },
  {
    id: 'sms3',
    name: 'Membership Rollover Alert',
    content: 'Good news {name}! Your {credits} unused credits have rolled over to next month.',
    category: 'Membership'
  }
];

const AdminSMS = () => {
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [twilioConfigured, setTwilioConfigured] = useState(false);

  useEffect(() => {
    checkTwilioConfig();
  }, []);

  const checkTwilioConfig = async () => {
    try {
      const result = await smsApi.testConfig();
      setTwilioConfigured(result.configured);
    } catch (err) {
      setTwilioConfigured(false);
    }
  };

  const handleSendTestSMS = async () => {
    if (!testPhone || !message) {
      toast({
        title: 'Missing Information',
        description: 'Please enter a phone number and message.',
        variant: 'destructive'
      });
      return;
    }

    setSending(true);
    try {
      const result = await smsApi.send(testPhone, message);
      if (result.success) {
        toast({
          title: 'SMS Sent!',
          description: `Test message sent successfully. SID: ${result.message_sid}`
        });
      }
    } catch (err) {
      toast({
        title: 'SMS Failed',
        description: err.response?.data?.detail || 'Failed to send SMS. Check Twilio configuration.',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendSMS = () => {
    toast({
      title: 'SMS Campaign Queued',
      description: `Your message has been queued for delivery to ${recipientType === 'all' ? 'all patients' : 'selected recipients'}. Note: Bulk sending requires verified numbers on Twilio trial account.`
    });
  };

  const handleTemplateSelect = (templateId) => {
    const template = smsTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setMessage(template.content);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">SMS Campaigns</h1>
        <p className="text-gray-600 mt-2">Send promotional messages and reminders to your patients</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* SMS Composer */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Create SMS Campaign</CardTitle>
            <CardDescription>Compose and send SMS messages to your patients</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Template Selector */}
            <div>
              <Label>Select Template (Optional)</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {smsTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recipients */}
            <div>
              <Label>Send To</Label>
              <Select value={recipientType} onValueChange={setRecipientType}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Patients</SelectItem>
                  <SelectItem value="members">Members Only</SelectItem>
                  <SelectItem value="inactive">Inactive Patients (90+ days)</SelectItem>
                  <SelectItem value="custom">Custom List</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-2">
                {message.length} / 160 characters
              </p>
            </div>

            {/* Variables Help */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Available Variables:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div><code className="bg-white px-2 py-1 rounded">{'{name}'}</code> - Patient name</div>
                <div><code className="bg-white px-2 py-1 rounded">{'{treatment}'}</code> - Treatment name</div>
                <div><code className="bg-white px-2 py-1 rounded">{'{time}'}</code> - Appointment time</div>
                <div><code className="bg-white px-2 py-1 rounded">{'{credits}'}</code> - Credit balance</div>
              </div>
            </div>

            <Button
              className=""
              onClick={handleSendSMS}
              disabled={!message}
            >
              <Send className="mr-2 h-4 w-4" />
              Send SMS Campaign
            </Button>
          </CardContent>
        </Card>

        {/* Templates & Stats */}
        <div className="space-y-6">
          {/* Twilio Status */}
          <Card className={twilioConfigured ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                {twilioConfigured ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
                <div>
                  <p className="font-semibold text-gray-900">
                    Twilio {twilioConfigured ? 'Connected' : 'Not Configured'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {twilioConfigured 
                      ? 'SMS functionality is ready.' 
                      : 'Add Twilio credentials in settings.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Test SMS */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Send Test SMS</CardTitle>
              <CardDescription>Test your SMS before sending campaigns</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Phone Number (E.164 format)</Label>
                <Input
                  placeholder="+15551234567"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Trial accounts can only send to verified numbers
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleSendTestSMS}
                disabled={sending || !twilioConfigured}
              >
                {sending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Test SMS
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Campaign Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Sent This Month</span>
                  <span className="text-sm font-semibold">1,247</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: '68%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Delivery Rate</span>
                  <span className="text-sm font-semibold">98.5%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '98.5%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Response Rate</span>
                  <span className="text-sm font-semibold">24.3%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '24.3%' }} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSMS;